import {
  DropDownRef,
  DropDownSelectBox,
  DropDownSelectBoxOption,
  TokenListItem,
  WalletTokenListItem,
} from '@/components';
import {
  TokenInterface,
  UnderlyingYieldBearingAssetInterface,
  WalletTokenInterface,
  WalletUnderlyingYieldBearingAssetInterface,
} from '@/interfaces';
import { logWarning } from '@/utils/logger';
import React, { useCallback, useMemo } from 'react';
import {
  FieldErrors,
  useForm,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
type SupportedAssetType =
  | WalletTokenInterface
  | WalletUnderlyingYieldBearingAssetInterface
  | TokenInterface
  | UnderlyingYieldBearingAssetInterface;
interface Props<T extends SupportedAssetType> {
  name: string;
  assetsToDisplay: T[];
  selectedAsset: T | null;
  onAssetSelect: (asset: T) => void;
  register?: UseFormRegister<any>;
  setValue?: UseFormSetValue<any>;
  clearErrors?: UseFormClearErrors<any>;
  errors?: FieldErrors;
  label?: string;
  tooltip?: JSX.Element;
  tooltipIsFullBleed?: boolean;
  showNetworkForOptions?: boolean;
  showBalanceForOptions?: boolean;
  showNetworkForSelected?: boolean;
  showBalanceForSelected?: boolean;
  showSubNetworkNextToMainNetwork?: boolean;
}
const TokenOrUnderlyingYieldBearingAssetDropDownSelectBox = React.forwardRef<
  DropDownRef,
  Props<SupportedAssetType>
>(
  (
    {
      name,
      label = 'Select an Asset',
      assetsToDisplay,
      selectedAsset,
      onAssetSelect,
      register,
      setValue,
      clearErrors,
      errors,
      tooltip,
      tooltipIsFullBleed = false,
      showNetworkForOptions = true,
      showBalanceForOptions = true,
      showNetworkForSelected = true,
      showBalanceForSelected = true,
      showSubNetworkNextToMainNetwork = true,
      ...rest
    },
    ref,
  ) => {
    const form = useForm();
    const safeRegister = register || form.register;
    const safeSetValue = setValue || form.setValue;
    const safeClearErrors = clearErrors || form.clearErrors;
    const dropdownOptions = useMemo(
      () =>
        assetsToDisplay.map((asset) => ({
          value: asset.id,
        })),
      [assetsToDisplay],
    );
    const getListItem = useCallback(
      (asset: SupportedAssetType, showNetwork: boolean, showBalance: boolean) =>
        'walletAddress' in asset || 'rewards' in asset ? (
          <WalletTokenListItem
            key={asset.id}
            token={asset as WalletTokenInterface}
            showWalletAddress={false}
            showBalance={showBalance}
            showNetwork={showNetwork}
            showNetworkPrefix
            showSubNetworkNextToMainNetwork={showSubNetworkNextToMainNetwork}
          />
        ) : (
          <TokenListItem
            key={asset.id}
            token={asset as TokenInterface}
            showNetwork={showNetwork}
            showNetworkPrefix
            showSubNetworkNextToMainNetwork={showSubNetworkNextToMainNetwork}
          />
        ),
      [showSubNetworkNextToMainNetwork],
    );
    const displayOptions = useMemo(
      () =>
        assetsToDisplay.map((asset) =>
          getListItem(asset, showNetworkForOptions, showBalanceForOptions),
        ),
      [
        assetsToDisplay,
        getListItem,
        showNetworkForOptions,
        showBalanceForOptions,
      ],
    );
    const selectedDisplayOptions = useMemo(
      () =>
        assetsToDisplay.map((asset) =>
          getListItem(asset, showNetworkForSelected, showBalanceForSelected),
        ),
      [
        assetsToDisplay,
        getListItem,
        showNetworkForSelected,
        showBalanceForSelected,
      ],
    );
    const handleAssetSelect = useCallback(
      (
        param:
          | React.ChangeEvent<HTMLInputElement>
          | DropDownSelectBoxOption<string>,
      ) => {
        if ('value' in param) {
          const selectedAsset = assetsToDisplay.find(
            (asset) => asset.id === param.value,
          );
          if (selectedAsset) {
            safeSetValue(name, selectedAsset, {
              shouldValidate: true,
              shouldDirty: true,
            });
            onAssetSelect(selectedAsset);
            safeClearErrors(name);
          }
        } else {
          logWarning(
            'Unexpected event type received in handleAssetSelect',
            'TokenOrUnderlyingYieldBearingAssetDropDownSelectBox',
            { param },
          );
        }
      },
      [onAssetSelect, assetsToDisplay],
    );
    return (
      <DropDownSelectBox
        ref={ref}
        label={label}
        name={name}
        options={dropdownOptions}
        displayOptions={displayOptions}
        selectedDisplayOptions={selectedDisplayOptions}
        value={selectedAsset?.id || ''}
        onChange={handleAssetSelect as any}
        register={safeRegister}
        setValue={safeSetValue}
        clearErrors={safeClearErrors}
        errors={errors}
        tooltip={tooltip}
        tooltipIsFullBleed={tooltipIsFullBleed}
        commitPrimitiveValueToForm={false}
        {...rest}
      />
    );
  },
);
export default React.memo(TokenOrUnderlyingYieldBearingAssetDropDownSelectBox);
